import React, { FocusEvent, ReactNode } from 'react';
import {
  TextField as MuiTextField,
  SxProps,
  InputAdornment,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Color } from '~/theme/index.ts';
import { OverridableStyles } from '~/utils/types.ts';

export interface TextFieldProps extends OverridableStyles {
  autoComplete?: string;
  color?: Color;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  id?: string;
  label?: string;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onReset?: () => void;
  placeholder?: string;
  required?: boolean;
  size?: 'medium' | 'small';
  type?: string;
  value?: string;
  variant?: 'filled' | 'outlined' | 'standard';
  startIcon?: ReactNode;
  dataTestId?: string;
}

const TextField: React.FC<TextFieldProps> = React.forwardRef<
  HTMLInputElement,
  TextFieldProps
>((props, ref) => {
  const {
    autoComplete,
    color = 'secondary',
    disabled,
    error,
    fullWidth,
    helperText,
    id,
    label,
    name,
    onChange,
    placeholder,
    required,
    size = 'medium',
    type,
    value,
    variant = 'outlined',
    startIcon,
    styleOverrides,
    onBlur,
    onReset,
    dataTestId,
    ...rest
  } = props;

  const sx: SxProps = {
    '& .MuiFormLabel-root': {
      fontSize: styleOverrides?.fontSize,
    },

    '& .MuiInputBase-root': {
      fontSize: styleOverrides?.fontSize,
      backgroundColor: styleOverrides?.backgroundColor,
      borderRadius: styleOverrides?.borderRadius,
      '&::-webkit-search-cancel-button': {
        WebkitAppearance: 'none',
      },

      '& fieldset': {
        borderColor: styleOverrides?.borderColor,
        borderWidth: styleOverrides?.borderWidth,
      },
    },

    '& .MuiInputBase-input': {
      height: styleOverrides?.height,
      padding: styleOverrides?.padding,
    },

    flex: styleOverrides?.flex,
    width: styleOverrides?.width,
  };

  return (
    <MuiTextField
      data-test-id={dataTestId}
      autoComplete={autoComplete}
      color={color}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      helperText={helperText}
      id={id}
      label={label}
      name={name}
      placeholder={placeholder}
      required={required}
      size={size}
      type={type}
      value={value}
      variant={variant}
      InputProps={{
        startAdornment: startIcon && (
          <InputAdornment position="start">{startIcon}</InputAdornment>
        ),
        endAdornment: type === 'search' && value && (
          <InputAdornment
            onClick={onReset}
            position="end"
            sx={{
              color: 'var(--commander-colorTextSecondary)',
              cursor: 'pointer',
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </InputAdornment>
        ),
      }}
      sx={sx}
      ref={ref}
      onChange={onChange}
      onBlur={(e) => (onBlur ? onBlur(e) : undefined)}
      {...rest}
    />
  );
});

TextField.displayName = 'TextField';

export default TextField;
